import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as SecurityManagementActions from '../../state/security-management.actions';
import {
  IChildUserRoleDetails,
  IEditUserRemoveActionState,
  IUserPreferencesResponse,
  IEditUserRemoveRequestData,
  IEditUserActiveSecuritySet,
  TAddEditUserScreenType
} from '../../state/security-management.model';
import { ConcatAndCapitalizeFullName } from '../../../../utils/concatAndCapitalize';
import { filter } from 'rxjs/operators';
import {
  selectEditUserAction,
  selectEditUserActiveSetAction,
  selectCurrentScreen
} from '../../state/security-management.selectors';
import { Helpers } from 'src/app/utils/helpers';

@Component({
  selector: 'app-user-role-demographic',
  templateUrl: './user-role-demographic.component.html',
  styleUrls: ['./user-role-demographic.component.scss']
})
export class UserRoleDemographicComponent implements OnInit, OnChanges {
  @Input() isEditOn;
  @Input() isAddUserEditOn;
  @Input() assignUserRoleInfo: IChildUserRoleDetails;
  @Input() preferredUserName;
  @Input() securitySets: any[];

  securityGroupId: string;
  userName: string;
  openEditUserPopup = false;
  isOaaUser = false;
  isLoading = false;
  isLoadingAnimation = true;
  isSuccess = null;
  isError = false;
  groupIds: any[];
  groupIdLength: number;
  getUserName: string;
  screenType: TAddEditUserScreenType;
  isFromPendinguser = false;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  static DemographicsUserNameKey = 'demographics-userName';
  static DemographicsSecurityGroupIdKey = 'demographics-securityGroupId';

  removeUserModalData = {
    class: 'popUpWrap',
    id: 'removeUserModal',
    role: 'alertdialog',
    ariaLabelledby: 'userModalHeading',
    modal: {
      class: 'popUp'
    },
    header: {
      class: 'popUp-header',
      title: {
        text: 'Remove User',
        id: 'userModalHeading'
      },
      inlineBtn: {
        class: 'ods-btn ods-btn-ghost icon-only close on-dark-background',
        id: 'oaaCLoseModalButton'
      }
    },
    footer: {
      class: 'popUp-footer',
      buttons: [
        {
          class: 'ods-btn ods-btn-primary popUp-footer-lastbtn',
          text: 'Remove',
          id: 1
        },
        {
          class: 'ods-btn ods-btn-primary outline',
          text: 'Cancel',
          id: 2
        }
      ]
    }
  };

  deactivateUserModalData = Helpers.copy(this.removeUserModalData);

  oaaUserModalData = {
    class: 'popUpWrap',
    id: 'oAAUserModal',
    role: 'alertdialog',
    ariaLabelledby: 'userModalHeading',
    modal: {
      class: 'popUp'
    },
    header: {
      class: 'popUp-header',
      title: {
        text: 'Remove User',
        id: 'userModalHeading'
      },
      inlineBtn: {
        class: 'ods-btn ods-btn-ghost icon-only close on-dark-background',
        id: 'oaaCLoseModalButton'
      }
    },
    footer: {
      class: 'popUp-footer',
      buttons: [
        {
          class: 'ods-btn ods-btn-primary popUp-footer-lastbtn',
          text: 'Manage OAA',
          id: 1
        },
        {
          class: 'ods-btn ods-btn-primary outline',
          text: 'Cancel',
          id: 2
        }
      ]
    }
  };
  removeEditUser$ = this.store.select(selectEditUserAction).pipe(takeUntil(this.ngUnsubscribe));
  activeEditUser$ = this.store.select(selectEditUserActiveSetAction).pipe(takeUntil(this.ngUnsubscribe));
  currentScreen$ = this.store.select(selectCurrentScreen).pipe(takeUntil(this.ngUnsubscribe));

  constructor(private store: Store, private router: Router, private route: ActivatedRoute) {
    this.deactivateUserModalData.id = 'deactivateUserModal';
    this.deactivateUserModalData.header.title.text = 'Deactivating User';
  }

  ngOnInit(): void {
    

    this.route.queryParams.pipe(filter((value) => value.securityGroupId && (value.userName || value.invitationId))).subscribe((value: any) => {
      sessionStorage.setItem(UserRoleDemographicComponent.DemographicsSecurityGroupIdKey, value.securityGroupId);
      sessionStorage.setItem(UserRoleDemographicComponent.DemographicsUserNameKey, value.userName);
      this.securityGroupId = value.securityGroupId;
      this.isFromPendinguser = value?.from && value.from === 'pendingUser' ? true :false
    });
    this.currentScreen$.subscribe((state) => {
      this.screenType = state;
    });
  }

  ngOnChanges(): void {
    if(this.assignUserRoleInfo) {
      this.userName = ConcatAndCapitalizeFullName(this.assignUserRoleInfo?.userPreferences);
      this.isLoadingAnimation = false;
    }
  }

  // when Edit-User module is active: click editIcon to addUser page
  // EditUserflow, UserProfile, AddUserflow ahve access to click "Edit[Icon]" Editing user demographic info:
  navigateTo() {
    const AddUserUrl = this.isAddUserEditOn && this.router.url.includes('add-user/security-set-selection') ? '/security-management/add-user/security-set-selection/add-user-search/assign-user-role' : '/security-management/add-user/add-user-search/assign-user-role';
    const editUserUrl = this.isEditOn && this.router.url.includes('edit-user/multi-sets') ? '/security-management/edit-user/multi-sets/edit-existing-user' : '/security-management/edit-user/edit-existing-user';
    const baseUrl = this.isEditOn
      ? editUserUrl
      : this.isAddUserEditOn
      ? AddUserUrl
      : '';
    this.router.navigate([baseUrl], {
      queryParams: {
        screenType: 'edit-user-demographic',
        previousScreenType: this.screenType
      }
    });
    const payload: IUserPreferencesResponse = this.assignUserRoleInfo.userPreferences;
    this.store.dispatch(SecurityManagementActions.setEditExistingUserDemographic({ demographic: payload }));
  }

  removeEditUser() {
    let ids = [];
    ids.push(this.securityGroupId);
    const payload: IEditUserRemoveRequestData = {
      securityGroupIds: ids,
      preferredUserName: this.preferredUserName
    };
    this.store.dispatch(SecurityManagementActions.removeEditUser({ editUserRemoveRequest: payload }));
    this.removeEditUser$.subscribe((state: IEditUserRemoveActionState) => {
      this.isSuccess = state?.isSuccess;
      this.isLoading = state?.isLoading;
      if (this.isSuccess === true) {
        this.store.dispatch(SecurityManagementActions.resetEditUserSearch());
        this.router.navigate(['/security-management/edit-user']);
      }
    });
    this.closePopUp();
  }

  removeEditUserPopUp(): void {
    this.store.dispatch(SecurityManagementActions.activeEditUser({ activeEditUserName: this.preferredUserName }));
    this.activeEditUser$.subscribe((state: IEditUserActiveSecuritySet) => {
      this.isLoading = state?.isLoading;
      this.groupIds = state?.results;
      this.groupIdLength = state?.results?.length;
      if (state?.results) {
        const hasOaaUser = this.groupIds?.find((x: any) => x?.securityGroupId === this.securityGroupId);
        this.isOaaUser = hasOaaUser?.isOaa === 'false' ? false : true;
        this.openEditUserPopup = !this.isOaaUser;
      }
    });
  }

  closePopUp(): void {
    this.openEditUserPopup = false;
    this.isOaaUser = false;
  }

  navigateToOaa() {
    this.router.navigate(['/security-management/manage-oaa']);
  }
}
